<template>
  <div class="erp-consum-page">
    <card :title="$t('tools.erp_integration.consum')" :hide-title="true" id="erp-consum">
      <div class="consum-header">
        <h1 class="consum-header__title">{{ $t("tools.erp_integration.consum") }}</h1>
        <div class="consum-header__actions">
          <router-link
            to="/tools/erp-integration"
            class="btn btn-md btn-secondary consum-header__icon-button"
            :aria-label="$t('common.back_to_list')"
            :title="$t('common.back_to_list')"
          >
            <span class="dx-icon dx-icon-arrowleft" aria-hidden="true"></span>
          </router-link>
          <button
            type="button"
            class="btn btn-md btn-secondary consum-header__icon-button"
            :disabled="loadingQueue"
            :aria-label="$t('common.refresh')"
            :title="$t('common.refresh')"
            @click="reloadActiveView"
          >
            <span class="dx-icon dx-icon-refresh" aria-hidden="true"></span>
          </button>
        </div>
      </div>

      <div class="view-switcher" role="tablist">
        <button
          type="button"
          class="view-switcher__btn"
          :class="{ active: activeView === 'approved' }"
          role="tab"
          :aria-selected="activeView === 'approved'"
          @click="activeView = 'approved'"
        >
          {{ $t("tools.erp_integration.approved_consums") }}
        </button>
        <button
          type="button"
          class="view-switcher__btn"
          :class="{ active: activeView === 'queue' }"
          role="tab"
          :aria-selected="activeView === 'queue'"
          @click="switchToQueue"
        >
          {{ $t("tools.erp_integration.consum_view_queue") }}
        </button>
        <button
          type="button"
          class="view-switcher__btn"
          :class="{ active: activeView === 'audit' }"
          role="tab"
          :aria-selected="activeView === 'audit'"
          @click="activeView = 'audit'"
        >
          {{ $t("tools.erp_integration.consum_view_audit") }}
        </button>
        <button
          type="button"
          class="view-switcher__btn"
          :class="{ active: activeView === 'work-logs' }"
          role="tab"
          :aria-selected="activeView === 'work-logs'"
          @click="activeView = 'work-logs'"
        >
          {{ $t("tools.erp_integration.consum_work_invoice_logs") }}
        </button>
        <button
          type="button"
          class="view-switcher__btn"
          :class="{ active: activeView === 'settings' }"
          role="tab"
          :aria-selected="activeView === 'settings'"
          @click="activeView = 'settings'"
        >
          {{ $t("tools.erp_integration.consum_view_settings") }}
        </button>
      </div>

      <!-- 1. APPROVED CONSUIMS VIEW -->
      <div v-if="activeView === 'approved'" class="view-panel">
        <ApprovedConsumsGrid ref="approvedConsumsGrid" @open-audit="onOpenAudit" />
      </div>

      <!-- 2. QUEUE MONITOR VIEW -->
      <div v-if="activeView === 'queue'" class="view-panel">
        <div class="queue-filters">
          <input
            v-model="queueFilters.search"
            type="text"
            class="form-control"
            :placeholder="$t('tools.erp_integration.consum_search_placeholder')"
            @keyup.enter="loadQueue"
          />
          <select v-model="queueFilters.status" class="form-control" @change="loadQueue">
            <option value="">{{ $t("common.all") }}</option>
            <option v-for="item in queueStatusOptions" :key="item" :value="item">{{ item }}</option>
          </select>
          <button
            type="button"
            class="btn btn-sm btn-outline-primary queue-filter-button"
            :aria-label="$t('common.search')"
            :title="$t('common.search')"
            :disabled="loadingQueue"
            @click="loadQueue"
          >
            <i class="fa fa-search" aria-hidden="true"></i>
          </button>
          <button
            type="button"
            class="btn btn-sm btn-outline-secondary queue-filter-button"
            :aria-label="$t('tools.erp_integration.consum_bulk_retry')"
            :title="$t('tools.erp_integration.consum_bulk_retry')"
            :disabled="loadingQueue"
            @click="runBulkRetry"
          >
            <i class="fa fa-redo" aria-hidden="true"></i>
          </button>
          <button
            type="button"
            class="btn btn-sm btn-outline-danger queue-filter-button"
            :aria-label="$t('tools.erp_integration.consum_bulk_cancel_waiting')"
            :title="$t('tools.erp_integration.consum_bulk_cancel_waiting')"
            :disabled="loadingQueue"
            @click="runBulkCancelWaiting"
          >
            <i class="fa fa-times-circle" aria-hidden="true"></i>
          </button>
        </div>

        <DxDataGrid
          class="smagria-grid erp-consum-grid"
          :data-source="queue"
          key-expr="uuid"
          :show-column-lines="true"
          :show-row-lines="false"
          :show-borders="true"
          :row-alternation-enabled="true"
          :column-auto-width="false"
          :allow-column-resizing="true"
          :no-data-text="$t('common.no_data_text')"
          style="min-height: 320px"
          @selection-changed="onQueueSelectionChanged"
          @row-expanding="onQueueRowExpanding"
        >
          <DxMasterDetail :enabled="true" template="queueAuditDetail" />
          <DxPaging :page-size="20" />
          <DxPager
            :visible="true"
            :allowed-page-sizes="[10, 20, 50, 100]"
            display-mode="compact"
            :info-text="$t('common.page_info')"
            :show-page-size-selector="true"
            :show-info="true"
            :show-navigation-buttons="true"
          />
          <DxSelection mode="single" />
          <DxScrolling mode="standard" column-rendering-mode="standard" show-scrollbar="always" :use-native="true" />
          <DxColumn data-field="updated_at" :caption="$t('common.last_update')" data-type="datetime" :sort-order="'desc'" />
          <DxColumn data-field="created_at" :caption="$t('common.created_at')" data-type="datetime" />
          <DxColumn data-field="sent_at" :caption="$t('tools.erp_integration.consum_erp_sent_at')" data-type="datetime" />
          <DxColumn data-field="uuid" :caption="$t('tools.erp_integration.consum_erp_batch')" cell-template="batchCell" />
          <template #batchCell="{ data }">
            <span :title="data.data.uuid">{{ data.data.uuid.slice(0, 8) }} ({{ data.data.item_count ?? data.data.consum_count }})</span>
          </template>
          <DxColumn data-field="status" :caption="$t('common.status')" cell-template="statusCell" />
          <template #statusCell="{ data }">
            <span class="badge" :class="getStatusBadgeClass(data.data.status)">
              {{ data.data.status }}
            </span>
          </template>
          <DxColumn data-field="wm_document_number" :caption="$t('tools.erp_integration.consum_wm_document')" cell-template="docCell" />
          <template #docCell="{ data }">
            <span>{{ formatDocNumber(data.data) }}</span>
          </template>
          <DxColumn data-field="sm_document_codes" :caption="$t('tools.erp_integration.consum_sm_document')" cell-template="smDocumentCell" />
          <template #smDocumentCell="{ data }">
            <span>{{ formatSmDocumentCodes(data.data) }}</span>
          </template>
          <DxColumn data-field="item_count" :caption="$t('tools.erp_integration.consum_erp_summary_items_count')" width="90" />
          <DxColumn data-field="total_quantity" :caption="$t('common.quantity')" />
          <DxColumn data-field="warehouse_names" :caption="$t('common.warehouse')" />
          <DxColumn data-field="department_names" :caption="$t('works.work_approval.unit_name')" />
          <DxColumn data-field="attempt_count" :caption="$t('tools.erp_integration.reception_attempts')" width="90" />
          <DxColumn data-field="last_error_code" :caption="$t('tools.erp_integration.reception_last_error')" cell-template="errorCell" />
          <template #errorCell="{ data }">
            <span :title="data.data.last_error_message || data.data.last_error_code" :class="{ 'text-danger': !!data.data.last_error_code }">
              {{ data.data.last_error_code || '-' }}
            </span>
          </template>
          <DxColumn type="buttons" width="200">
            <DxButton icon="find" :hint="$t('tools.erp_integration.consum_view_audit')" :on-click="onOpenAudit" />
            <DxButton icon="check" :hint="$t('tools.erp_integration.consum_validate')" :on-click="onValidateQueueItem" />
            <DxButton icon="export" :hint="$t('tools.erp_integration.consum_send')" :on-click="onSendQueueItem" :visible="isQueueItemSendable" />
            <DxButton icon="refresh" :hint="$t('tools.erp_integration.consum_retry')" :on-click="onRetryQueueItem" />
            <DxButton icon="close" :hint="$t('tools.erp_integration.consum_cancel')" :on-click="onCancelQueueItem" />
          </DxColumn>
          <template #queueAuditDetail="{ data }">
            <div class="queue-audit-detail-container">
              <div v-if="queueAuditLoading[data.data.uuid]" class="text-muted p-3">
                <i class="fa fa-spinner fa-spin me-1"></i>
                {{ $t("common.please_wait") }}
              </div>
              <div v-else-if="queueAuditErrors[data.data.uuid]" class="alert alert-danger mb-2">
                {{ queueAuditErrors[data.data.uuid] }}
                <button type="button" class="btn btn-sm btn-outline-secondary ml-2" @click="loadQueueAudit(data.data, true)">
                  {{ $t("common.refresh") }}
                </button>
              </div>
              <div v-else class="queue-audit-detail-wrap p-2">
              <!-- 1. Invoice audit -->
              <div v-if="getQueueItemInvoices(data.data.uuid).length" class="audit-invoices-section mb-3">
                <h6 class="font-weight-bold mb-2 text-primary">
                  <i class="fa fa-file-invoice me-1"></i>
                  {{ $t("tools.erp_integration.consum_feedback_invoice_results") }} ({{ getQueueItemInvoices(data.data.uuid).length }} db)
                </h6>
                <div
                  v-for="(invoice, invIdx) in getQueueItemInvoices(data.data.uuid)"
                  :key="'q-inv-' + (invoice.uuid || invIdx)"
                  class="audit-invoice-card mb-3"
                >
                  <div class="audit-invoice-header d-flex justify-content-between align-items-center p-2 bg-light border rounded-top">
                    <div class="d-flex align-items-center">
                      <strong>#{{ invIdx + 1 }} {{ invoice.owner_unit_name || invoice.parcel_legal_entity_name || invoice.company_name || "" }}</strong>
                      <span class="badge ms-2" :class="getInvoiceStatusBadgeClass(invoice.status)">
                        {{ invoiceStatusLabel(invoice.status) }}
                      </span>
                      <span v-if="invoice.iesiri_doc_number" class="badge badge-light border ms-2 text-dark">
                        NrDoc Ieșire: {{ invoice.iesiri_doc_number }}
                      </span>
                      <span v-if="invoice.intrari_payload && invoice.intrari_doc_number" class="badge badge-light border ms-2 text-dark">
                        {{ invoice.iesiri_payload?.TipDocument === "FACTURA IESIRE" ? "NrNIR" : "NrDoc Intrare" }}: {{ invoice.intrari_doc_number }}
                      </span>
                      <span v-if="invoice.wm_cod_ies" class="ms-2 text-muted">CodIes: {{ invoice.wm_cod_ies }}</span>
                    </div>
                    <div v-if="invoice.last_error" class="text-danger small font-weight-bold">
                      {{ invoice.last_error }}
                    </div>
                  </div>

                  <!-- Missing mappings / issues table for invoice -->
                  <div v-if="(invoice.issues && invoice.issues.length) || (invoice.missing_mappings && invoice.missing_mappings.length)" class="p-2 bg-warning-subtle border-start border-end border-warning">
                    <div class="d-flex align-items-center mb-1 text-warning-emphasis font-weight-bold">
                      <i class="fa fa-exclamation-triangle me-1"></i>
                      {{ $t("tools.erp_integration.consum_invoice_missing_mappings_title", { count: (invoice.issues || invoice.missing_mappings).length }) }}
                    </div>
                    <div class="table-responsive">
                      <table class="table table-sm table-bordered bg-white mb-0">
                        <thead class="thead-light">
                          <tr>
                            <th style="width: 15%">{{ $t("tools.erp_integration.consum_feedback_missing_col_type") }}</th>
                            <th style="width: 25%">{{ $t("tools.erp_integration.consum_feedback_missing_col_name") }}</th>
                            <th style="width: 15%">{{ $t("tools.erp_integration.consum_feedback_missing_col_field") }}</th>
                            <th style="width: 35%">{{ $t("tools.erp_integration.consum_feedback_missing_col_reason") }}</th>
                            <th style="width: 10%; text-align: center;">{{ $t("tools.erp_integration.consum_feedback_missing_col_action") }}</th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr v-for="(iss, iIdx) in (invoice.issues || invoice.missing_mappings)" :key="iIdx">
                            <td><span class="badge badge-info">{{ iss.type }}</span></td>
                            <td><strong>{{ displayIssueName(iss) }}</strong></td>
                            <td><code>{{ iss.field }}</code></td>
                            <td class="text-danger">{{ displayIssueReason(iss) }}</td>
                            <td style="text-align: center;">
                              <button
                                type="button"
                                class="btn btn-xs btn-outline-primary"
                                :title="$t('tools.erp_integration.consum_feedback_missing_btn_configure')"
                                @click="goToMapping(iss)"
                              >
                                <i class="fa fa-wrench me-1"></i> {{ $t("tools.erp_integration.consum_feedback_missing_btn_configure") }}
                              </button>
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  </div>

                  <InvoiceAuditTabs
                    :invoice="invoice"
                    :iesiri-url="getInvoiceIesiriUrl(invoice, data.data.uuid)"
                    :intrari-url="getInvoiceIntrariUrl(invoice, data.data.uuid)"
                    :format-audit-json="formatAuditJson"
                    :format-json="formatJson"
                    @copy="copyJson"
                  />
                </div>
              </div>

              <!-- 2. Consum Payload / Response (if available) -->
              <div v-if="getQueueItemPayload(data.data.uuid) || getQueueItemResponse(data.data.uuid)" class="audit-payload-section mb-3">
                <div class="audit-payload-split">
                  <div v-if="getQueueItemPayload(data.data.uuid)" class="payload-col">
                    <div class="d-flex justify-content-between align-items-center mb-2 flex-wrap gap-2">
                      <div class="d-flex align-items-center flex-wrap gap-2">
                        <h6 class="mb-0 font-weight-bold">
                          {{ $t("tools.erp_integration.consum_sent_payload") }}
                        </h6>
                        <div class="endpoint-badge">
                          <span class="badge-method">POST</span>
                          <code>{{ getQueueItemUrl(data.data.uuid) }}</code>
                        </div>
                      </div>
                    </div>
                    <div class="json-viewer-container">
                      <button
                        type="button"
                        class="btn btn-sm btn-outline-light json-copy-button"
                        :title="$t('common.copy')"
                        :aria-label="$t('common.copy')"
                        @click="copyJson(getQueueItemPayload(data.data.uuid))"
                      >
                        <i class="fa fa-copy" aria-hidden="true"></i>
                      </button>
                      <pre class="json-viewer">{{ formatJson(getQueueItemPayload(data.data.uuid)) }}</pre>
                    </div>
                  </div>
                  <div v-if="getQueueItemResponse(data.data.uuid)" class="payload-col">
                    <div class="d-flex justify-content-between align-items-center mb-2 flex-wrap gap-2">
                      <div class="d-flex align-items-center flex-wrap gap-2">
                        <h6 class="mb-0 font-weight-bold">
                          {{ $t("tools.erp_integration.consum_response_payload") }}
                        </h6>
                        <div class="endpoint-badge">
                          <code>{{ getQueueItemUrl(data.data.uuid) }}</code>
                        </div>
                      </div>
                    </div>
                    <div class="json-viewer-container">
                      <button
                        type="button"
                        class="btn btn-sm btn-outline-light json-copy-button"
                        :title="$t('common.copy')"
                        :aria-label="$t('common.copy')"
                        @click="copyJson(getQueueItemResponse(data.data.uuid))"
                      >
                        <i class="fa fa-copy" aria-hidden="true"></i>
                      </button>
                      <pre class="json-viewer">{{ formatJson(getQueueItemResponse(data.data.uuid)) }}</pre>
                    </div>
                  </div>
                </div>
              </div>

              <!-- 3. Audit events grid -->
              <h6 class="font-weight-bold mb-2">
                <i class="fa fa-history me-1"></i>
                {{ $t("tools.erp_integration.consum_view_audit") }}
              </h6>
              <DxDataGrid
                class="smagria-grid erp-consum-grid"
                :data-source="getQueueItemEvents(data.data.uuid)"
                key-expr="id"
                :show-column-lines="true"
                :show-row-lines="false"
                :show-borders="true"
                :row-alternation-enabled="true"
                :column-auto-width="false"
                :allow-column-resizing="true"
                :word-wrap-enabled="true"
                :no-data-text="$t('common.no_data_text')"
                style="min-height: 320px"
              >
                <DxMasterDetail :enabled="true" template="queueAuditEventDetail" />
                <DxColumn data-field="created_at" :caption="$t('common.created_at')" data-type="datetime" width="160" />
                <DxColumn data-field="event_type" :caption="$t('tools.erp_integration.consum_event_type')" width="200" />
                <DxColumn data-field="previous_status" :caption="$t('tools.erp_integration.consum_previous_status')" width="120" />
                <DxColumn data-field="new_status" :caption="$t('tools.erp_integration.consum_new_status')" width="120" />
                <DxColumn data-field="message" :caption="$t('common.message')" />
                <template #queueAuditEventDetail="{ data: event }">
                  <div class="audit-event-detail-wrapper p-3 bg-light border rounded">
                    <div v-if="getEventIssues(event).length" class="alert alert-warning mb-2">
                      <div class="font-weight-bold mb-1">
                        <i class="fa fa-exclamation-triangle me-1"></i>
                        {{ $t("tools.erp_integration.consum_feedback_missing_mappings", { count: getEventIssues(event).length }) }}
                      </div>
                      <div class="table-responsive">
                        <table class="table table-sm table-bordered bg-white mb-0">
                          <thead class="thead-light">
                            <tr>
                              <th>{{ $t("tools.erp_integration.consum_feedback_missing_col_type") }}</th>
                              <th>{{ $t("tools.erp_integration.consum_feedback_missing_col_name") }}</th>
                              <th>{{ $t("tools.erp_integration.consum_feedback_missing_col_field") }}</th>
                              <th>{{ $t("tools.erp_integration.consum_feedback_missing_col_reason") }}</th>
                              <th style="width: 100px; text-align: center;">{{ $t("tools.erp_integration.consum_feedback_missing_col_action") }}</th>
                            </tr>
                          </thead>
                          <tbody>
                            <tr v-for="(iss, issIdx) in getEventIssues(event)" :key="issIdx">
                              <td><span class="badge badge-info">{{ iss.type }}</span></td>
                              <td><strong>{{ displayIssueName(iss) }}</strong></td>
                              <td><code>{{ iss.field }}</code></td>
                              <td class="text-danger">{{ displayIssueReason(iss) }}</td>
                              <td style="text-align: center;">
                                <button
                                  type="button"
                                  class="btn btn-xs btn-outline-primary"
                                  @click="goToMapping(iss)"
                                >
                                  <i class="fa fa-wrench me-1"></i> {{ $t("tools.erp_integration.consum_feedback_missing_btn_configure") }}
                                </button>
                              </td>
                            </tr>
                          </tbody>
                        </table>
                      </div>
                    </div>
                    <div class="audit-payload-split">
                      <div class="payload-col">
                        <div class="d-flex justify-content-between align-items-center mb-2 flex-wrap gap-2">
                          <div class="d-flex align-items-center flex-wrap gap-2">
                            <h6 class="mb-0 font-weight-bold">{{ $t("tools.erp_integration.consum_sent_payload") }}</h6>
                            <div v-if="getEventUrl(event)" class="endpoint-badge">
                              <span class="badge-method">{{ getEventMethod(event) }}</span>
                              <code>{{ getEventUrl(event) }}</code>
                            </div>
                          </div>
                        </div>
                        <div class="json-viewer-container">
                          <button
                            v-if="getEventRequest(event)"
                            type="button"
                            class="btn btn-sm btn-outline-light json-copy-button"
                            :title="$t('common.copy')"
                            :aria-label="$t('common.copy')"
                            @click="copyJson(getEventRequest(event))"
                          >
                            <i class="fa fa-copy" aria-hidden="true"></i>
                          </button>
                          <pre class="json-viewer">{{ formatAuditJson(getEventRequest(event)) }}</pre>
                        </div>
                      </div>
                      <div class="payload-col">
                        <div class="d-flex justify-content-between align-items-center mb-2 flex-wrap gap-2">
                          <div class="d-flex align-items-center flex-wrap gap-2">
                            <h6 class="mb-0 font-weight-bold">{{ $t("tools.erp_integration.consum_response_payload") }}</h6>
                            <div v-if="getEventUrl(event)" class="endpoint-badge">
                              <code>{{ getEventUrl(event) }}</code>
                            </div>
                          </div>
                        </div>
                        <div class="json-viewer-container">
                          <button
                            v-if="getEventResponse(event)"
                            type="button"
                            class="btn btn-sm btn-outline-light json-copy-button"
                            :title="$t('common.copy')"
                            :aria-label="$t('common.copy')"
                            @click="copyJson(getEventResponse(event))"
                          >
                            <i class="fa fa-copy" aria-hidden="true"></i>
                          </button>
                          <pre class="json-viewer">{{ formatAuditJson(getEventResponse(event)) }}</pre>
                        </div>
                      </div>
                    </div>
                    <div v-if="getEventIntrariRequest(event)" class="mt-2 payload-col">
                      <div class="d-flex justify-content-between align-items-center mb-2 flex-wrap gap-2">
                        <div class="d-flex align-items-center flex-wrap gap-2">
                          <h6 class="mb-0 font-weight-bold">{{ $t("tools.erp_integration.consum_invoice_incoming_payload") }}</h6>
                          <div class="endpoint-badge">
                            <span class="badge-method">POST</span>
                            <code>{{ getEventIntrariUrl(event) }}</code>
                          </div>
                        </div>
                      </div>
                      <div class="json-viewer-container">
                        <button
                          type="button"
                          class="btn btn-sm btn-outline-light json-copy-button"
                          :title="$t('common.copy')"
                          :aria-label="$t('common.copy')"
                          @click="copyJson(getEventIntrariRequest(event))"
                        >
                          <i class="fa fa-copy" aria-hidden="true"></i>
                        </button>
                        <pre class="json-viewer">{{ formatAuditJson(getEventIntrariRequest(event)) }}</pre>
                      </div>
                    </div>
                  </div>
                </template>
              </DxDataGrid>
            </div>
            </div>
          </template>
        </DxDataGrid>
      </div>

      <!-- 3. AUDIT VIEW -->
      <div v-if="activeView === 'audit'" class="view-panel">
        <div v-if="!selectedQueueItem" class="placeholder-panel">
          <h5>{{ $t("tools.erp_integration.consum_audit_empty_title") }}</h5>
          <p>{{ $t("tools.erp_integration.consum_audit_empty_description") }}</p>
        </div>

        <div v-else class="audit-wrap">
          <div class="audit-actions d-flex justify-content-between align-items-center mb-2">
            <div class="d-flex align-items-center">
              <button
                type="button"
                class="btn btn-sm btn-outline-secondary me-2"
                @click="switchToQueue"
              >
                <i class="fa fa-arrow-left me-1"></i>
                {{ $t("common.back") }}
              </button>
              <button
                type="button"
                class="btn btn-sm btn-outline-primary"
                @click="reloadActiveView"
              >
                <i class="fa fa-sync-alt me-1"></i>
                {{ $t("common.refresh") }}
              </button>
            </div>
            <div class="d-flex align-items-center">
              <button
                v-if="selectedQueueItem.status !== 'CONFIRMED' && selectedQueueItem.status !== 'CANCELED'"
                type="button"
                class="btn btn-sm btn-danger me-2"
                @click="onCancelQueueItem(selectedQueueItem)"
              >
                <i class="fa fa-times-circle me-1"></i>
                {{ $t("tools.erp_integration.consum_cancel") }}
              </button>
              <button
                v-if="isQueueItemRetryable(selectedQueueItem)"
                type="button"
                class="btn btn-sm btn-outline-warning me-2"
                @click="onRetryQueueItem(selectedQueueItem)"
              >
                <i class="fa fa-redo me-1"></i>
                {{ $t("tools.erp_integration.consum_retry") }}
              </button>
              <button
                v-if="selectedQueueItem.status !== 'CONFIRMED' && selectedQueueItem.status !== 'PARTIAL_SUCCESS'"
                type="button"
                class="btn btn-sm btn-primary"
                @click="onSendQueueItem(selectedQueueItem)"
              >
                <i class="fa fa-paper-plane me-1"></i>
                {{ $t("tools.erp_integration.consum_send") }}
              </button>
            </div>
          </div>

          <div v-if="selectedQueueItem.status === 'RETRY_WAITING'" class="alert alert-warning d-flex justify-content-between align-items-center mt-1 mb-2">
            <div>
              <i class="fa fa-clock me-1"></i>
              <strong>{{ $t("tools.erp_integration.consum_status_waiting_title") }}</strong>:
              <span>{{ $t("tools.erp_integration.consum_status_waiting_notice") }}</span>
            </div>
            <button
              type="button"
              class="btn btn-sm btn-danger ms-3"
              @click="onCancelQueueItem(selectedQueueItem)"
            >
              <i class="fa fa-times-circle me-1"></i>
              {{ $t("tools.erp_integration.consum_cancel") }}
            </button>
          </div>

          <div v-if="selectedQueueItem.status === 'CANCELED'" class="alert alert-secondary mt-1 mb-2">
            <i class="fa fa-info-circle me-1"></i>
            {{ $t("tools.erp_integration.consum_status_canceled_notice") }}
          </div>

          <div class="audit-head">
            <div><strong>UUID:</strong> {{ selectedQueueItem.uuid }}</div>
            <div><strong>{{ $t("tools.erp_integration.consum_erp_batch") }}:</strong> {{ selectedQueueItem.uuid }} ({{ selectedQueueItem.item_count ?? selectedQueueItem.consum_count }})</div>
            <div><strong>{{ $t("tools.erp_integration.consum_erp_sent_at") }}:</strong> {{ selectedQueueItem.sent_at || '-' }}</div>
            <div><strong>{{ $t("tools.erp_integration.consum_wm_document") }}:</strong> {{ formatDocNumber(selectedQueueItem) }}</div>
            <div><strong>{{ $t("tools.erp_integration.consum_sm_document") }}:</strong> {{ formatSmDocumentCodes(selectedQueueItem) }}</div>
            <div><strong>CodIes:</strong> {{ selectedQueueItem.wm_cod_ies || '-' }}</div>
            <div><strong>{{ $t("tools.erp_integration.consum_erp_summary_items_count") }}:</strong> {{ selectedQueueItem.item_count ?? selectedQueueItem.consum_count ?? 0 }}</div>
            <div><strong>{{ $t("common.quantity") }}:</strong> {{ selectedQueueItem.total_quantity }}</div>
            <div><strong>{{ $t("common.warehouse") }}:</strong> {{ selectedQueueItem.warehouse_names || '-' }}</div>
            <div><strong>{{ $t("works.work_approval.unit_name") }}:</strong> {{ selectedQueueItem.department_names || '-' }}</div>
            <div><strong>{{ $t("common.last_update") }}:</strong> {{ selectedQueueItem.updated_at || selectedQueueItem.created_at }}</div>
            <div><strong>{{ $t("common.status") }}:</strong> <span class="badge" :class="getStatusBadgeClass(selectedQueueItem.status)">{{ selectedQueueItem.status }}</span></div>
          </div>

          <div v-if="selectedQueueItem.last_error_message" class="alert alert-danger mt-2 mb-2">
            <strong>{{ selectedQueueItem.last_error_code || $t('error') }}:</strong>
            <div>{{ selectedQueueItem.last_error_message }}</div>
          </div>

          <!-- JSON Payload Sent to ERP & Response from ERP -->
          <div v-if="selectedQueueItem.payload_json || selectedQueueItem.wm_response_json" class="audit-payload-section mt-2 mb-3">
            <div class="audit-payload-split">
              <div v-if="selectedQueueItem.payload_json" class="payload-col">
                <div class="d-flex justify-content-between align-items-center mb-2 flex-wrap gap-2">
                  <div class="d-flex align-items-center flex-wrap gap-2">
                    <h6 class="mb-0 font-weight-bold">{{ $t("tools.erp_integration.consum_sent_payload") }}</h6>
                    <div class="endpoint-badge">
                      <span class="badge-method">POST</span>
                      <code>{{ getSelectedQueueUrl() }}</code>
                    </div>
                  </div>
                </div>
                <div class="json-viewer-container">
                  <button
                    type="button"
                    class="btn btn-sm btn-outline-light json-copy-button"
                    :title="$t('common.copy')"
                    :aria-label="$t('common.copy')"
                    @click="copyJson(selectedQueueItem.payload_json)"
                  >
                    <i class="fa fa-copy" aria-hidden="true"></i>
                  </button>
                  <pre class="json-viewer">{{ formatJson(selectedQueueItem.payload_json) }}</pre>
                </div>
              </div>

              <div v-if="selectedQueueItem.wm_response_json" class="payload-col">
                <div class="d-flex justify-content-between align-items-center mb-2 flex-wrap gap-2">
                  <div class="d-flex align-items-center flex-wrap gap-2">
                    <h6 class="mb-0 font-weight-bold">{{ $t("tools.erp_integration.consum_response_payload") }}</h6>
                    <div class="endpoint-badge">
                      <code>{{ getSelectedQueueUrl() }}</code>
                    </div>
                  </div>
                </div>
                <div class="json-viewer-container">
                  <button
                    type="button"
                    class="btn btn-sm btn-outline-light json-copy-button"
                    :title="$t('common.copy')"
                    :aria-label="$t('common.copy')"
                    @click="copyJson(selectedQueueItem.wm_response_json)"
                  >
                    <i class="fa fa-copy" aria-hidden="true"></i>
                  </button>
                  <pre class="json-viewer">{{ formatJson(selectedQueueItem.wm_response_json) }}</pre>
                </div>
              </div>
            </div>
          </div>

          <!-- Invoice audit -->
          <div v-if="selectedQueueItem.invoices && selectedQueueItem.invoices.length" class="audit-invoices-section mb-3">
            <h6 class="font-weight-bold mb-2">
              <i class="fa fa-file-invoice me-1"></i>
              {{ $t("tools.erp_integration.consum_feedback_invoice_results") }}
            </h6>
            <div
              v-for="(invoice, invIdx) in selectedQueueItem.invoices"
              :key="'inv-' + (invoice.uuid || invIdx)"
              class="audit-invoice-card mb-3"
            >
              <div class="audit-invoice-header d-flex justify-content-between align-items-center p-2 bg-light border rounded-top">
                <div class="d-flex align-items-center">
                  <strong>#{{ invIdx + 1 }} {{ invoice.owner_unit_name || invoice.parcel_legal_entity_name || invoice.company_name || "" }}</strong>
                  <span class="badge ms-2" :class="getInvoiceStatusBadgeClass(invoice.status)">
                    {{ invoiceStatusLabel(invoice.status) }}
                  </span>
                  <span v-if="invoice.iesiri_doc_number" class="badge badge-light border ms-2 text-dark">
                    NrDoc Ieșire: {{ invoice.iesiri_doc_number }}
                  </span>
                  <span v-if="invoice.intrari_payload && invoice.intrari_doc_number" class="badge badge-light border ms-2 text-dark">
                    {{ invoice.iesiri_payload?.TipDocument === "FACTURA IESIRE" ? "NrNIR" : "NrDoc Intrare" }}: {{ invoice.intrari_doc_number }}
                  </span>
                  <span v-if="invoice.wm_cod_ies" class="ms-2 text-muted">CodIes: {{ invoice.wm_cod_ies }}</span>
                </div>
                <div v-if="invoice.last_error" class="text-danger small font-weight-bold">
                  {{ invoice.last_error }}
                </div>
              </div>

              <!-- Missing mappings / issues table for invoice -->
              <div v-if="(invoice.issues && invoice.issues.length) || (invoice.missing_mappings && invoice.missing_mappings.length)" class="p-2 bg-warning-subtle border-start border-end border-warning">
                <div class="d-flex align-items-center mb-1 text-warning-emphasis font-weight-bold">
                  <i class="fa fa-exclamation-triangle me-1"></i>
                  {{ $t("tools.erp_integration.consum_invoice_missing_mappings_title", { count: (invoice.issues || invoice.missing_mappings).length }) }}
                </div>
                <div class="table-responsive">
                  <table class="table table-sm table-bordered bg-white mb-0">
                    <thead class="thead-light">
                      <tr>
                        <th style="width: 15%">{{ $t("tools.erp_integration.consum_feedback_missing_col_type") }}</th>
                        <th style="width: 25%">{{ $t("tools.erp_integration.consum_feedback_missing_col_name") }}</th>
                        <th style="width: 15%">{{ $t("tools.erp_integration.consum_feedback_missing_col_field") }}</th>
                        <th style="width: 35%">{{ $t("tools.erp_integration.consum_feedback_missing_col_reason") }}</th>
                        <th style="width: 10%; text-align: center;">{{ $t("tools.erp_integration.consum_feedback_missing_col_action") }}</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="(iss, iIdx) in (invoice.issues || invoice.missing_mappings)" :key="iIdx">
                        <td><span class="badge badge-info">{{ iss.type }}</span></td>
                        <td><strong>{{ displayIssueName(iss) }}</strong></td>
                        <td><code>{{ iss.field }}</code></td>
                        <td class="text-danger">{{ displayIssueReason(iss) }}</td>
                        <td style="text-align: center;">
                          <button
                            type="button"
                            class="btn btn-xs btn-outline-primary"
                            :title="$t('tools.erp_integration.consum_feedback_missing_btn_configure')"
                            @click="goToMapping(iss)"
                          >
                            <i class="fa fa-wrench me-1"></i> {{ $t("tools.erp_integration.consum_feedback_missing_btn_configure") }}
                          </button>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>

              <InvoiceAuditTabs
                :invoice="invoice"
                :iesiri-url="getInvoiceIesiriUrl(invoice, selectedQueueItem.uuid)"
                :intrari-url="getInvoiceIntrariUrl(invoice, selectedQueueItem.uuid)"
                :format-audit-json="formatAuditJson"
                :format-json="formatJson"
                @copy="copyJson"
              />
            </div>
          </div>

          <DxDataGrid
            class="smagria-grid erp-consum-grid"
            :data-source="selectedQueueItem.events || []"
            key-expr="id"
            :show-column-lines="true"
            :show-row-lines="false"
            :show-borders="true"
            :row-alternation-enabled="true"
            :column-auto-width="false"
            :allow-column-resizing="true"
            :word-wrap-enabled="true"
            :no-data-text="$t('common.no_data_text')"
            style="min-height: 320px"
          >
            <DxMasterDetail :enabled="true" template="auditEventDetail" />
            <DxPaging :page-size="20" />
            <DxPager
              :visible="true"
              :allowed-page-sizes="[10, 20, 50]"
              display-mode="compact"
              :info-text="$t('common.page_info')"
              :show-page-size-selector="true"
              :show-info="true"
              :show-navigation-buttons="true"
            />
            <DxScrolling mode="standard" column-rendering-mode="standard" show-scrollbar="always" :use-native="true" />
            <DxColumn data-field="created_at" :caption="$t('common.created_at')" data-type="datetime" width="160" />
            <DxColumn data-field="event_type" :caption="$t('tools.erp_integration.consum_event_type')" width="160" />
            <DxColumn data-field="previous_status" :caption="$t('tools.erp_integration.consum_previous_status')" width="120" />
            <DxColumn data-field="new_status" :caption="$t('tools.erp_integration.consum_new_status')" width="120" />
            <DxColumn data-field="message" :caption="$t('common.message')" cell-template="auditMessageCell" />
            <template #auditMessageCell="{ data }">
              <div class="audit-message-cell">
                {{ data.data.message }}
              </div>
            </template>
            <DxColumn data-field="actor_type" :caption="$t('common.user')" width="100" />
            <template #auditEventDetail="{ data: event }">
              <div class="audit-event-detail-wrapper p-3 bg-light border rounded">
                <div v-if="getEventIssues(event).length" class="alert alert-warning mb-2">
                  <div class="font-weight-bold mb-1">
                    <i class="fa fa-exclamation-triangle me-1"></i>
                    {{ $t("tools.erp_integration.consum_feedback_missing_mappings", { count: getEventIssues(event).length }) }}
                  </div>
                  <div class="table-responsive">
                    <table class="table table-sm table-bordered bg-white mb-0">
                      <thead class="thead-light">
                        <tr>
                          <th>{{ $t("tools.erp_integration.consum_feedback_missing_col_type") }}</th>
                          <th>{{ $t("tools.erp_integration.consum_feedback_missing_col_name") }}</th>
                          <th>{{ $t("tools.erp_integration.consum_feedback_missing_col_field") }}</th>
                          <th>{{ $t("tools.erp_integration.consum_feedback_missing_col_reason") }}</th>
                          <th style="width: 100px; text-align: center;">{{ $t("tools.erp_integration.consum_feedback_missing_col_action") }}</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr v-for="(iss, issIdx) in getEventIssues(event)" :key="issIdx">
                          <td><span class="badge badge-info">{{ iss.type }}</span></td>
                          <td><strong>{{ displayIssueName(iss) }}</strong></td>
                          <td><code>{{ iss.field }}</code></td>
                          <td class="text-danger">{{ displayIssueReason(iss) }}</td>
                          <td style="text-align: center;">
                            <button
                              type="button"
                              class="btn btn-xs btn-outline-primary"
                              @click="goToMapping(iss)"
                            >
                              <i class="fa fa-wrench me-1"></i> {{ $t("tools.erp_integration.consum_feedback_missing_btn_configure") }}
                            </button>
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
                <div class="audit-payload-split">
                  <div class="payload-col">
                    <div class="d-flex justify-content-between align-items-center mb-2 flex-wrap gap-2">
                      <div class="d-flex align-items-center flex-wrap gap-2">
                        <h6 class="mb-0 font-weight-bold">{{ $t("tools.erp_integration.consum_sent_payload") }}</h6>
                        <div v-if="getEventUrl(event)" class="endpoint-badge">
                          <span class="badge-method">{{ getEventMethod(event) }}</span>
                          <code>{{ getEventUrl(event) }}</code>
                        </div>
                      </div>
                    </div>
                    <div class="json-viewer-container">
                      <button
                        v-if="getEventRequest(event)"
                        type="button"
                        class="btn btn-sm btn-outline-light json-copy-button"
                        :title="$t('common.copy')"
                        :aria-label="$t('common.copy')"
                        @click="copyJson(getEventRequest(event))"
                      >
                        <i class="fa fa-copy" aria-hidden="true"></i>
                      </button>
                      <pre class="json-viewer">{{ formatAuditJson(getEventRequest(event)) }}</pre>
                    </div>
                  </div>
                  <div class="payload-col">
                    <div class="d-flex justify-content-between align-items-center mb-2 flex-wrap gap-2">
                      <div class="d-flex align-items-center flex-wrap gap-2">
                        <h6 class="mb-0 font-weight-bold">{{ $t("tools.erp_integration.consum_response_payload") }}</h6>
                        <div v-if="getEventUrl(event)" class="endpoint-badge">
                          <code>{{ getEventUrl(event) }}</code>
                        </div>
                      </div>
                    </div>
                    <div class="json-viewer-container">
                      <button
                        v-if="getEventResponse(event)"
                        type="button"
                        class="btn btn-sm btn-outline-light json-copy-button"
                        :title="$t('common.copy')"
                        :aria-label="$t('common.copy')"
                        @click="copyJson(getEventResponse(event))"
                      >
                        <i class="fa fa-copy" aria-hidden="true"></i>
                      </button>
                      <pre class="json-viewer">{{ formatAuditJson(getEventResponse(event)) }}</pre>
                    </div>
                  </div>
                </div>
                <div v-if="getEventIntrariRequest(event)" class="mt-2 payload-col">
                  <div class="d-flex justify-content-between align-items-center mb-2 flex-wrap gap-2">
                    <div class="d-flex align-items-center flex-wrap gap-2">
                      <h6 class="mb-0 font-weight-bold">{{ $t("tools.erp_integration.consum_invoice_incoming_payload") }}</h6>
                      <div class="endpoint-badge">
                        <span class="badge-method">POST</span>
                        <code>{{ getEventIntrariUrl(event) }}</code>
                      </div>
                    </div>
                  </div>
                  <div class="json-viewer-container">
                    <button
                      type="button"
                      class="btn btn-sm btn-outline-light json-copy-button"
                      :title="$t('common.copy')"
                      :aria-label="$t('common.copy')"
                      @click="copyJson(getEventIntrariRequest(event))"
                    >
                      <i class="fa fa-copy" aria-hidden="true"></i>
                    </button>
                    <pre class="json-viewer">{{ formatAuditJson(getEventIntrariRequest(event)) }}</pre>
                  </div>
                </div>
              </div>
            </template>
          </DxDataGrid>
        </div>
      </div>

      <!-- 4. SETTINGS VIEW -->
      <div v-if="activeView === 'work-logs'" class="view-panel">
        <ErpSyncLogPanel ref="workInvoiceLogs" entity-code="WORK_INVOICES" :visible="true" />
      </div>

      <div v-if="activeView === 'settings'" class="view-panel">
        <ConsumSettingsPanel />
      </div>
    </card>
  </div>
</template>

<script>
import DxDataGrid, {
  DxColumn,
  DxSelection,
  DxButton,
  DxScrolling,
  DxPaging,
  DxPager,
  DxMasterDetail,
} from "devextreme-vue/data-grid";
import notify from "devextreme/ui/notify";
import { mapActions, mapGetters } from "vuex";
import ApprovedConsumsGrid from "@/pages/tools/erp-integration/consum/components/ApprovedConsumsGrid.vue";
import ConsumSettingsPanel from "@/pages/tools/erp-integration/consum/components/ConsumSettingsPanel.vue";
import InvoiceAuditTabs from "@/pages/tools/erp-integration/consum/components/InvoiceAuditTabs.vue";
import ErpSyncLogPanel from "@/pages/tools/erp-integration/components/ErpSyncLogPanel.vue";
import { issueDisplayName, issueDisplayReason } from "@/pages/tools/erp-integration/consum/issueDisplay";

export default {
  name: "ErpConsumPage",
  components: {
    DxDataGrid,
    DxColumn,
    DxSelection,
    DxButton,
    DxScrolling,
    DxPaging,
    DxPager,
    DxMasterDetail,
    ApprovedConsumsGrid,
    ConsumSettingsPanel,
    InvoiceAuditTabs,
    ErpSyncLogPanel,
  },
  data() {
    return {
      activeView: "approved",
      loadingQueue: false,
      selectedQueueItem: null,
      queueAuditByUuid: {},
      queueAuditLoading: {},
      queueAuditErrors: {},
      queueFilters: {
        search: "",
        status: "",
      },
      queueStatusOptions: [
        "NEW",
        "VALIDATED",
        "SENT",
        "CONFIRMED",
        "ERROR",
        "RETRY_WAITING",
        "DEAD",
        "CANCELED",
      ],
    };
  },
  computed: {
    ...mapGetters("erpIntegration", {
      queue: "consumQueue",
      queuePagination: "consumQueuePagination",
      storeSelectedItem: "consumSelectedItem",
    }),
  },
  watch: {
    "$route.query.queue_uuid": {
      immediate: true,
      handler(uuid) {
        if (typeof uuid === "string" && uuid) {
          this.onOpenAudit({ uuid });
        }
      },
    },
    storeSelectedItem(newItem) {
      if (newItem) {
        this.selectedQueueItem = newItem;
      }
    },
  },
  methods: {
    displayIssueName(issue) {
      return issueDisplayName(issue, this.$t("tools.erp_integration.consum_feedback_unknown_entry"));
    },
    displayIssueReason(issue) {
      return issueDisplayReason(issue, this.$t("tools.erp_integration.consum_feedback_unknown_entry"));
    },
    ...mapActions("erpIntegration", {
      fetchConsumQueue: "fetchConsumQueue",
      fetchConsumQueueItem: "fetchConsumQueueItem",
      validateConsumQueueItem: "validateConsumQueueItem",
      sendConsumQueueItem: "sendConsumQueueItem",
      retryConsumQueueItem: "retryConsumQueueItem",
      cancelConsumQueueItem: "cancelConsumQueueItem",
      bulkRetryConsumQueue: "bulkRetryConsumQueue",
      bulkCancelConsumQueue: "bulkCancelConsumQueue",
    }),
    async switchToQueue() {
      this.activeView = "queue";
      if (!this.queue || this.queue.length === 0) {
        await this.loadQueue();
      }
    },
    async loadQueue() {
      this.loadingQueue = true;
      try {
        await this.fetchConsumQueue(this.queueFilters);
        const expandedAuditUuids = Object.keys(this.queueAuditByUuid);
        this.queueAuditByUuid = {};
        await Promise.all(expandedAuditUuids.map((uuid) => this.loadQueueAudit({ uuid }, true)));
      } catch (e) {
        notify(e.message || this.$t("tools.erp_integration.consum_action_error"), "error", 3000);
      } finally {
        this.loadingQueue = false;
      }
    },
    async reloadActiveView() {
      if (this.activeView === "approved") {
        this.$refs.approvedConsumsGrid?.reload();
      } else if (this.activeView === "queue") {
        await this.loadQueue();
      } else if (this.activeView === "work-logs") {
        await this.$refs.workInvoiceLogs?.reload();
      } else if (this.activeView === "audit" && this.selectedQueueItem?.uuid) {
        await this.fetchConsumQueueItem(this.selectedQueueItem.uuid);
      }
    },
    async onQueueSelectionChanged(event) {
      const selected = event.selectedRowsData?.[0] || null;
      if (!selected?.uuid) {
        return;
      }
      try {
        const item = await this.fetchConsumQueueItem(selected.uuid);
        this.selectedQueueItem = item;
      } catch (e) {
        notify(e.message || this.$t("tools.erp_integration.consum_action_error"), "error", 3000);
      }
    },
    onQueueRowExpanding(event) {
      let itemData = event.data;
      if (!itemData && event.component?.getVisibleRows) {
        const row = event.component.getVisibleRows().find((visibleRow) => visibleRow.key === event.key);
        itemData = row?.data;
      }
      if (!itemData && event.key) {
        itemData = typeof event.key === "object" ? event.key : { uuid: event.key };
      }
      if (itemData) {
        this.loadQueueAudit(itemData);
      }
    },
    async loadQueueAudit(item, force = false) {
      const uuid = item?.uuid;
      if (!uuid || this.queueAuditLoading[uuid] || (!force && this.queueAuditByUuid[uuid])) {
        return;
      }

      this.$set(this.queueAuditLoading, uuid, true);
      this.$delete(this.queueAuditErrors, uuid);
      try {
        const audit = await this.fetchConsumQueueItem({ uuid, setSelected: false });
        if (!audit || !Array.isArray(audit.events)) {
          throw new Error(this.$t("tools.erp_integration.consum_audit_load_error"));
        }
        this.$set(this.queueAuditByUuid, uuid, audit);
      } catch (error) {
        this.$set(this.queueAuditErrors, uuid, error?.response?.data?.message || error?.message || this.$t("tools.erp_integration.consum_audit_load_error"));
      } finally {
        this.$set(this.queueAuditLoading, uuid, false);
      }
    },
    async onOpenAudit(event) {
      const item = event.row?.data || (event?.uuid ? event : this.selectedQueueItem);
      if (!item?.uuid) {
        return;
      }
      try {
        const fullItem = await this.fetchConsumQueueItem(item.uuid);
        this.selectedQueueItem = fullItem;
        this.activeView = "audit";
      } catch (e) {
        notify(e.message || this.$t("tools.erp_integration.consum_action_error"), "error", 3000);
      }
    },
    async onValidateQueueItem(event) {
      const item = event.row?.data || (event?.uuid ? event : this.selectedQueueItem);
      if (!item?.uuid) return;
      try {
        await this.validateConsumQueueItem({ uuid: item.uuid });
        await this.loadQueue();
        if (this.selectedQueueItem?.uuid === item.uuid) {
          await this.fetchConsumQueueItem(item.uuid);
        }
        notify(this.$t("tools.erp_integration.consum_validate_done"), "success", 2000);
      } catch (e) {
        notify(e.message || this.$t("tools.erp_integration.consum_action_error"), "error", 3000);
      }
    },
    async onSendQueueItem(event) {
      const item = event.row?.data || (event?.uuid ? event : this.selectedQueueItem);
      if (!item?.uuid) return;
      if (item.status === "PARTIAL_SUCCESS" || (item.status === "CONFIRMED" && this.hasIncompleteInvoices(item))) {
        await this.onRetryQueueItem(item);
        return;
      }
      try {
        await this.sendConsumQueueItem({ uuid: item.uuid, force: false });
        await this.loadQueue();
        if (this.selectedQueueItem?.uuid === item.uuid) {
          await this.fetchConsumQueueItem(item.uuid);
        }
        notify(this.$t("tools.erp_integration.consum_send_done"), "success", 2000);
      } catch (e) {
        await this.loadQueue();
        notify(e.message || this.$t("tools.erp_integration.consum_action_error"), "error", 3000);
      }
    },
    isQueueItemSendable(event) {
      const item = event?.row?.data;
      return item?.status !== "CONFIRMED" && item?.status !== "PARTIAL_SUCCESS";
    },
    isQueueItemRetryable(item) {
      return ["RETRY_WAITING", "ERROR", "DEAD", "PARTIAL_SUCCESS"].includes(item?.status) ||
        (item?.status === "CONFIRMED" && this.hasIncompleteInvoices(item));
    },
    hasIncompleteInvoices(item) {
      return (item?.invoices || []).some(
        (invoice) => !["NOT_INTER_SUBUNIT", "CONFIRMED"].includes(invoice?.status)
      );
    },
    async onRetryQueueItem(event) {
      const item = event.row?.data || (event?.uuid ? event : this.selectedQueueItem);
      if (!item?.uuid) return;
      try {
        await this.retryConsumQueueItem({
          uuid: item.uuid,
          mode: item.status === "PARTIAL_SUCCESS" || (item.status === "CONFIRMED" && this.hasIncompleteInvoices(item))
            ? "IMMEDIATE"
            : "SCHEDULE",
        });
        await this.loadQueue();
        if (this.selectedQueueItem?.uuid === item.uuid) {
          await this.fetchConsumQueueItem(item.uuid);
        }
        notify(this.$t("tools.erp_integration.consum_retry_done"), "success", 2000);
      } catch (e) {
        notify(e.message || this.$t("tools.erp_integration.consum_action_error"), "error", 3000);
      }
    },
    async onCancelQueueItem(event) {
      const item = event.row?.data || (event?.uuid ? event : this.selectedQueueItem);
      if (!item?.uuid) return;

      const promptMsg = this.$t("tools.erp_integration.consum_cancel_reason_prompt");
      const reason = window.prompt(promptMsg, this.$t("tools.erp_integration.consum_cancel_reason_default"));
      if (!reason) return;

      try {
        await this.cancelConsumQueueItem({ uuid: item.uuid, reason });
        await this.loadQueue();
        if (this.selectedQueueItem?.uuid === item.uuid) {
          await this.fetchConsumQueueItem(item.uuid);
        }
        notify(this.$t("tools.erp_integration.consum_cancel_done"), "success", 2000);
      } catch (e) {
        notify(e.message || this.$t("tools.erp_integration.consum_action_error"), "error", 3000);
      }
    },
    async runBulkRetry() {
      try {
        await this.bulkRetryConsumQueue();
        await this.loadQueue();
        notify(this.$t("tools.erp_integration.consum_bulk_retry_done"), "success", 2000);
      } catch (e) {
        notify(e.message || this.$t("tools.erp_integration.consum_action_error"), "error", 3000);
      }
    },
    async runBulkCancelWaiting() {
      const confirmed = window.confirm(
        this.$t("tools.erp_integration.consum_bulk_cancel_waiting_confirm")
      );
      if (!confirmed) return;

      const promptMsg = this.$t("tools.erp_integration.consum_cancel_reason_prompt");
      const reason = window.prompt(promptMsg, this.$t("tools.erp_integration.consum_bulk_cancel_reason_default"));
      if (!reason) return;

      try {
        const result = await this.bulkCancelConsumQueue({
          status_list: ["RETRY_WAITING", "ERROR", "DEAD"],
          reason,
        });
        await this.loadQueue();
        if (this.selectedQueueItem?.uuid) {
          await this.fetchConsumQueueItem(this.selectedQueueItem.uuid);
        }
        const count = result?.canceled_count ?? 0;
        notify(
          this.$t("tools.erp_integration.consum_bulk_cancel_done", { count }),
          "success",
          3000
        );
      } catch (e) {
        notify(e.message || this.$t("tools.erp_integration.consum_action_error"), "error", 3000);
      }
    },
    formatDocNumber(item) {
      if (!item) return "-";
      const parts = [item.wm_document_series, item.wm_document_number].filter(Boolean);
      return parts.length ? parts.join(" ") : "-";
    },
    formatSmDocumentCodes(item) {
      return Array.isArray(item?.sm_document_codes) && item.sm_document_codes.length
        ? item.sm_document_codes.join(", ")
        : item?.source_document_number || "-";
    },
    formatAuditJson(value) {
      return value === null || value === undefined || value === ""
        ? this.$t("tools.erp_integration.consum_audit_no_json")
        : this.formatJson(value);
    },
    getStatusBadgeClass(status) {
      switch (status) {
        case "CONFIRMED":
          return "badge-success";
        case "VALIDATED":
        case "SENT":
          return "badge-info";
        case "SKIPPED_CONSUM_FAILED":
        case "PARTIAL_SUCCESS":
          return "badge-warning";
        case "ERROR":
        case "DEAD":
          return "badge-danger";
        case "RETRY_WAITING":
          return "badge-secondary";
        case "CANCELED":
          return "badge-dark";
        default:
          return "badge-light";
      }
    },
    getInvoiceStatusBadgeClass(status) {
      switch (status) {
        case "CONFIRMED":
          return "badge-success";
        case "SENT":
        case "IESIRI_CONFIRMED":
        case "INTRARI_PREPARED":
          return "badge-info";
        case "INTRARI_IN_FLIGHT":
        case "IESIRI_IN_FLIGHT":
          return "badge-warning";
        case "INTRARI_BLOCKED":
        case "INTRARI_ERROR":
        case "INTRARI_UNKNOWN":
          return "badge-danger";
        case "BLOCKED":
          return "badge-danger";
        case "SKIPPED_CONSUM_FAILED":
          return "badge-warning";
        case "ERROR":
        case "UNKNOWN":
          return "badge-danger";
        case "PREPARED":
        case "PREPARING":
          return "badge-primary";
        case "PENDING":
        default:
          return "badge-secondary";
      }
    },
    goToMapping(issue) {
      let entityCode = "PRODUCTS";
      switch (issue?.type) {
        case "PRODUCT":
          entityCode = "PRODUCTS";
          break;
        case "WAREHOUSE":
          entityCode = "WAREHOUSES";
          break;
        case "MEASURE_UNIT":
          entityCode = "MEASURE_UNITS";
          break;
        case "SUBUNITATE":
        case "PARTNER":
        case "PARTNERS":
        case "ERP_PARTNER":
        case "LEGAL_ENTITY_PARTNER":
          entityCode = "LEGAL_ENTITIES";
          break;
        case "PARCEL":
        case "PARCEL_WAREHOUSE":
          entityCode = "PARCELS";
          break;
        case "WORK_TYPES":
          entityCode = "WORK_TYPES";
          break;
        default:
          entityCode = issue?.type || "PRODUCTS";
      }

      if (this.$router) {
        this.$router.push({
          path: "/tools/erp-integration/nomenclatures",
          query: { entity: entityCode, view: "mappings" },
        });
      }
    },
    invoiceStatusLabel(status) {
      const key = `tools.erp_integration.consum_invoice_status_${String(status || "").toLowerCase()}`;
      return this.$te(key) ? this.$t(key) : status;
    },
    formatJson(obj) {
      if (!obj) return "{}";
      try {
        return typeof obj === "string" ? JSON.stringify(JSON.parse(obj), null, 2) : JSON.stringify(obj, null, 2);
      } catch (e) {
        return String(obj);
      }
    },
    async copyJson(obj) {
      if (obj === null || obj === undefined) return;
      try {
        const text = typeof obj === "string" ? obj : JSON.stringify(obj, null, 2);
        if (navigator.clipboard?.writeText) {
          try {
            await navigator.clipboard.writeText(text);
          } catch {
            this.copyTextFallback(text);
          }
        } else {
          this.copyTextFallback(text);
        }
        notify(this.$t("common.copied"), "success", 2000);
      } catch {
        notify(this.$t("common.copy_failed"), "error", 4000);
      }
    },
    copyTextFallback(text) {
      const textarea = document.createElement("textarea");
      textarea.value = text;
      textarea.setAttribute("readonly", "");
      textarea.style.position = "fixed";
      textarea.style.opacity = "0";
      document.body.appendChild(textarea);
      try {
        textarea.focus();
        textarea.select();
        if (!document.execCommand("copy")) {
          throw new Error("Clipboard copy command failed");
        }
      } finally {
        document.body.removeChild(textarea);
      }
    },
    getQueueItemInvoices(uuid) {
      if (!uuid) return [];
      if (!this.queueAuditByUuid[uuid] && !this.queueAuditLoading[uuid] && !this.queueAuditErrors[uuid]) {
        this.loadQueueAudit({ uuid });
      }
      return this.queueAuditByUuid[uuid]?.invoices || [];
    },
    getQueueItemEvents(uuid) {
      if (!uuid) return [];
      if (!this.queueAuditByUuid[uuid] && !this.queueAuditLoading[uuid] && !this.queueAuditErrors[uuid]) {
        this.loadQueueAudit({ uuid });
      }
      return this.queueAuditByUuid[uuid]?.events || [];
    },
    getQueueItemPayload(uuid) {
      return this.queueAuditByUuid[uuid]?.payload_json || null;
    },
    getQueueItemResponse(uuid) {
      return this.queueAuditByUuid[uuid]?.wm_response_json || null;
    },
    getEventObj(event) {
      return event && event.data ? event.data : event;
    },
    getEventContext(event) {
      const ev = this.getEventObj(event);
      if (!ev) return {};
      if (typeof ev.context_json === "string") {
        try {
          return JSON.parse(ev.context_json);
        } catch (e) {
          return {};
        }
      }
      return ev.context_json || {};
    },
    getEventRequest(event) {
      const ctx = this.getEventContext(event);
      return ctx.request || null;
    },
    getEventResponse(event) {
      const ctx = this.getEventContext(event);
      return ctx.response || null;
    },
    getEventIntrariRequest(event) {
      const ctx = this.getEventContext(event);
      return ctx.intrari_request || null;
    },
    getEventIssues(event) {
      const ctx = this.getEventContext(event);
      return Array.isArray(ctx.issues) ? ctx.issues : [];
    },
    getQueueItemUrl(uuid) {
      const item = this.queueAuditByUuid[uuid] || (this.queue || []).find((i) => i.uuid === uuid);
      if (item?.endpoint_url) return item.endpoint_url;
      const sentEvent = (item?.events || []).find((e) => e.event_type === "SENT" || e.event_type === "EXPORT_SUCCESS" || e.event_type === "EXPORT_FAILED");
      const ctx = this.getEventContext(sentEvent);
      return ctx.url || ctx.endpoint || "/datasnap/rest/TServerMethods/BonuriConsum";
    },
    getSelectedQueueUrl() {
      if (!this.selectedQueueItem) return "/datasnap/rest/TServerMethods/BonuriConsum";
      if (this.selectedQueueItem.endpoint_url) return this.selectedQueueItem.endpoint_url;
      const sentEvent = (this.selectedQueueItem.events || []).find((e) => e.event_type === "SENT" || e.event_type === "EXPORT_SUCCESS" || e.event_type === "EXPORT_FAILED");
      const ctx = this.getEventContext(sentEvent);
      return ctx.url || ctx.endpoint || "/datasnap/rest/TServerMethods/BonuriConsum";
    },
    getInvoiceIesiriUrl(invoice, queueUuid = null) {
      if (invoice?.iesiri_endpoint_url) return invoice.iesiri_endpoint_url;
      if (invoice?.iesiri_payload?.TipDocument === "FACTURA IESIRE") {
        return "/datasnap/rest/TServerMethods/IesiriClienti";
      }
      const item = queueUuid ? (this.queueAuditByUuid[queueUuid] || (this.queue || []).find((i) => i.uuid === queueUuid)) : this.selectedQueueItem;
      const ev = (item?.events || []).find((e) =>
        ["INVOICE_IESIRI_SENT", "INVOICE_IESIRI_CONFIRMED", "INVOICE_IESIRI_FAILED", "INVOICE_BLOCKED"].includes(e.event_type) &&
        (!invoice?.uuid || this.getEventContext(e).invoice_uuid === invoice.uuid)
      );
      const ctx = this.getEventContext(ev);
      return ctx.iesiri_url || ctx.url || (invoice?.intrari_payload || invoice?.intrari_response
        ? "/datasnap/rest/TServerMethods/IesiriSubunit"
        : "/datasnap/rest/TServerMethods/IesiriClienti");
    },
    getInvoiceIntrariUrl(invoice, queueUuid = null) {
      if (invoice?.intrari_endpoint_url) return invoice.intrari_endpoint_url;
      if (invoice?.iesiri_payload?.TipDocument === "FACTURA IESIRE") {
        return "/datasnap/rest/TServerMethods/IntrariFurnizori";
      }
      const item = queueUuid ? (this.queueAuditByUuid[queueUuid] || (this.queue || []).find((i) => i.uuid === queueUuid)) : this.selectedQueueItem;
      const ev = (item?.events || []).find((e) =>
        ["INVOICE_INTRARI_SENT", "INVOICE_INTRARI_CONFIRMED", "INVOICE_INTRARI_FAILED", "INVOICE_BLOCKED"].includes(e.event_type) &&
        (!invoice?.uuid || this.getEventContext(e).invoice_uuid === invoice.uuid)
      );
      const ctx = this.getEventContext(ev);
      return ctx.intrari_url || (["INVOICE_INTRARI_SENT", "INVOICE_INTRARI_CONFIRMED", "INVOICE_INTRARI_FAILED"].includes(ev?.event_type) ? ctx.url : null) || "/datasnap/rest/TServerMethods/IntraridelaSubunitati";
    },
    getEventUrl(event) {
      const ctx = this.getEventContext(event);
      if (ctx.url) return ctx.url;
      if (ctx.endpoint) return ctx.endpoint;
      const ev = this.getEventObj(event);
      const type = ev?.event_type || "";
      if (type.includes("INVOICE_IESIRI")) return ctx.intrari_request || ctx.intrari_url
        ? "/datasnap/rest/TServerMethods/IesiriSubunit"
        : "/datasnap/rest/TServerMethods/IesiriClienti";
      if (type.includes("INVOICE_INTRARI")) return "/datasnap/rest/TServerMethods/IntraridelaSubunitati";
      if (type.includes("INVOICE_BLOCKED")) return ctx.iesiri_url || ctx.url || (ctx.intrari_request || ctx.intrari_url
        ? "/datasnap/rest/TServerMethods/IesiriSubunit"
        : "/datasnap/rest/TServerMethods/IesiriClienti");
      if (type === "SENT" || type === "EXPORT_SUCCESS" || type === "EXPORT_FAILED") return "/datasnap/rest/TServerMethods/BonuriConsum";
      return null;
    },
    getEventMethod(event) {
      const ctx = this.getEventContext(event);
      if (ctx.method) return ctx.method;
      return "POST";
    },
    getEventIntrariUrl(event) {
      const ctx = this.getEventContext(event);
      return ctx.intrari_url || "/datasnap/rest/TServerMethods/IntraridelaSubunitati";
    },
  },
};
</script>

<style lang="scss" scoped>
@import "@/assets/scss/components/smagriaTabs.scss";

.erp-consum-page ::v-deep .erp-consum-grid {
  height: auto;
}

::v-deep .dx-datagrid-rowsview .dx-scrollable-content {
  padding-bottom: 16px;
}

.consum-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 12px;
}

.consum-header__title {
  margin: 0;
  color: #323232;
  font-size: 32px;
  font-weight: 700;
  line-height: 1.2;
}

.consum-header__actions {
  display: flex;
  flex: 0 0 auto;
  gap: 8px;
}

.consum-header__icon-button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  padding: 0;
}

.view-switcher {
  @include smagria-tab-list;
  flex-wrap: wrap;
}

.view-switcher__btn {
  @include smagria-tab-button;
}

.view-panel {
  padding-top: 8px;
}

.queue-filters {
  display: grid;
  grid-template-columns: minmax(0, 2fr) minmax(120px, 1fr) repeat(3, 36px);
  gap: 8px;
  margin-bottom: 14px;
  width: 100%;
}

.queue-filters .form-control {
  min-width: 0;
}

.queue-filter-button {
  width: 36px;
  height: 36px;
  padding: 0;
}

@media (max-width: 575.98px) {
  .queue-filters {
    grid-template-columns: minmax(0, 1fr) minmax(100px, 0.7fr) repeat(3, 32px);
    gap: 6px;
  }

  .queue-filter-button {
    width: 32px;
    height: 32px;
  }
}

.audit-wrap {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.audit-head {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 10px;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  padding: 14px;
  font-size: 13px;
}

.audit-message-cell {
  white-space: pre-wrap;
  word-break: break-word;
  font-size: 12px;
}

.placeholder-panel {
  text-align: center;
  padding: 40px 20px;
  color: #64748b;
  background: #f8fafc;
  border-radius: 8px;
  border: 1px dashed #cbd5e1;
}

.badge {
  display: inline-block;
  padding: 4px 8px;
  font-size: 12px;
  font-weight: 600;
  border-radius: 4px;
}

.badge-success {
  background-color: #28a745;
  color: #fff;
}

.badge-danger {
  background-color: #dc3545;
  color: #fff;
}

.badge-warning {
  background-color: #ffc107;
  color: #212529;
}

.badge-info {
  background-color: #17a2b8;
  color: #fff;
}

.badge-secondary {
  background-color: #6c757d;
  color: #fff;
}

.badge-light {
  background-color: #e2e8f0;
  color: #475569;
}

.badge-dark {
  background-color: #343a40;
  color: #fff;
}

.audit-payload-split {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(320px, 1fr));
  gap: 16px;
}

.payload-col {
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  padding: 12px;
}

.json-viewer {
  background: #1e293b;
  color: #e2e8f0;
  padding: 42px 14px 14px;
  border-radius: 8px;
  font-family: monospace;
  font-size: 12px;
  max-height: 400px;
  overflow: auto;
  margin: 0;
}

.json-viewer-container {
  position: relative;
}

.json-copy-button {
  position: absolute;
  top: 8px;
  right: 8px;
  z-index: 1;
  padding: 4px 7px;
  line-height: 1;
}

.endpoint-badge {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: #f1f5f9;
  border: 1px solid #cbd5e1;
  border-radius: 4px;
  padding: 2px 7px;
  font-size: 11px;
}

.endpoint-badge .badge-method {
  background-color: #2563eb;
  color: #ffffff;
  padding: 1px 5px;
  border-radius: 3px;
  font-weight: 700;
  font-size: 10px;
  letter-spacing: 0.5px;
}

.endpoint-badge code {
  color: #0f172a;
  font-family: SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace;
  font-size: 11px;
  word-break: break-all;
}
</style>
